/**
 * Formats a timestamp to `MMM D,YYYY h:mm AM/PM` or `MMM D,YYYY h:mm:ss AM/PM`.
 *
 * @param {string|number|Date} timestamp - Timestamp value to format.
 * @param {{ includeSeconds?: boolean }} [options] - Formatting options.
 * @returns {string} Formatted timestamp string.
 *
 * @example
 * // Input
 * timestampFormatter("2026-05-05T15:47:24Z", { includeSeconds: true });
 * // Output
 * "May 5,2026 3:47:24 PM"
 *
 * @example
 * // Input
 * timestampFormatter("2026-05-05T15:47:24Z", { includeSeconds: false });
 * // Output
 * "May 5,2026 3:47 PM"
 */
export const timestampFormatter = (timestamp, options = {}) => {
  const { includeSeconds = true } = options;
  const date = new Date(timestamp);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12

  if (includeSeconds) {
    return `${month} ${day}, ${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  }
  return `${month} ${day}, ${year} ${hours}:${minutes} ${ampm}`;
};

export const TIMESTAMP_MINUTE_FORMAT = { includeSeconds: false };

/**
 * Sidebar-style sync label with minute precision (no seconds).
 *
 * @example
 * formatSyncedStatusLabel("2026-06-26T13:12:34Z");
 * // "Synced Jun 26, 2026 1:12 PM"
 */
export const formatSyncedStatusLabel = (timestamp) => {
  const formatted = timestampFormatter(timestamp, TIMESTAMP_MINUTE_FORMAT);
  if (!formatted || formatted === "Invalid Date") {
    return "";
  }
  return `Synced ${formatted}`;
};